import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import {
  assignRentCollector,
  getRentCollectorsByPropertyId,
  removeRentCollector,
} from '../../services/propertyService';
import { getAllUsers } from '../../services/userService';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';

interface AssignRentCollectorModalProps {
  isOpen: boolean;
  toggle: () => void;
  property;
  organizationId: number;
  onSuccess: () => void;
}

const schema = yup.object().shape({
  rentCollectorId: yup.string().required('Please select a rent collector'),
});

const AssignRentCollectorModal = ({
  isOpen,
  toggle,
  property,
  organizationId,
  onSuccess,
}: AssignRentCollectorModalProps) => {
  const [rentCollectors, setRentCollectors] = useState([]);
  const [assignedCollectors, setAssignedCollectors] = useState<any[]>([]);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      rentCollectorId: '',
    },
  });
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRentCollectors = async () => {
      dispatch(isVisible(true));
      try {
        const response = await getAllUsers(organizationId);

        if (response?.data?.rows?.length) {
          const list = response?.data?.rows?.map(user => user);
          const filteredList = list.filter(user =>
            JSON.parse(user.accessLevels).includes('Rent Collector'),
          );
          setRentCollectors(filteredList);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || 'Failed to fetch rent collectors',
        );
      } finally {
        dispatch(isVisible(false));
      }
    };

    if (isOpen) {
      fetchRentCollectors();
      // fetch assigned list
      (async () => {
        try {
          const res = await getRentCollectorsByPropertyId(property.id);
          if (res?.data) setAssignedCollectors(res.data);
        } catch (err) {
          toast.error(
            err?.response?.data?.message ||
              'Failed to fetch assigned rent collectors',
          );
        }
      })();
    }
  }, [isOpen]);

  const onSubmit = async data => {
    dispatch(isVisible(true));
    try {
      await assignRentCollector(property.id.toString(), data.rentCollectorId);
      toast.success('Rent collector assigned successfully');
      // reload assigned list
      const res = await getRentCollectorsByPropertyId(property.id);
      if (res?.data) setAssignedCollectors(res.data);
      onSuccess();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to assign rent collector',
      );
    } finally {
      dispatch(isVisible(false));
    }
  };

  const handleDelete = async (collectorId: string) => {
    if (!window.confirm('Remove this rent collector?')) return;
    dispatch(isVisible(true));
    try {
      await removeRentCollector(property.id.toString(), collectorId);
      toast.success('Removed');
      const res = await getRentCollectorsByPropertyId(property.id);
      if (res?.data) setAssignedCollectors(res.data);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove');
    } finally {
      dispatch(isVisible(false));
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Assign Rent Collector</ModalHeader>
      <ModalBody className="overflow-hidden">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup className="d-flex align-items-end gap-2">
            <div className="flex-grow-1">
              <Label for="rentCollectorId" className="form-label">
                Rent Collector
              </Label>
              <Controller
                name="rentCollectorId"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="select"
                    id="rentCollectorId"
                    className="form-control"
                    invalid={!!errors.rentCollectorId}
                  >
                    <option value="">Select</option>
                    {rentCollectors.map(collector => (
                      <option key={collector.id} value={collector.id}>
                        {collector.name}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.rentCollectorId && (
                <span className="text-danger">
                  {errors.rentCollectorId.message}
                </span>
              )}
            </div>
            <Button color="primary" type="submit" className="mt-2">
              Add
            </Button>
          </FormGroup>

          {/* List of assigned collectors */}
          {assignedCollectors.length > 0 && (
            <div className="mt-4">
              <h6>Assigned Rent Collectors</h6>
              {assignedCollectors.map(c => (
                <div
                  key={c.id}
                  className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
                >
                  <span>{c.name}</span>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDelete(c.id)}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default AssignRentCollectorModal;
